import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api from "../lib/api";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { PdfPreview } from "../components/PdfPreview";
import { ImageSquare, Quotes, PaperPlaneTilt, ChatCircleText, DownloadSimple } from "@phosphor-icons/react";

function ReplyBox({ token, capture }) {
  const { t } = useTranslation();
  const [reply, setReply] = useState(capture.reply || "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSubmit = async () => {
    if (!reply.trim()) return;
    setSaving(true);
    try {
      await api.patch(`/shares/${token}/captures/${capture.id}/reply`, { reply });
      setSaved(true);
      toast.success(t("sharePage.replySubmittedToast"));
    } catch (e) {
      toast.error(t("sharePage.replyFailedError"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2 border-t border-border pt-3" data-testid={`share-reply-box-${capture.id}`}>
      <p className="font-mono-tag text-[10px] text-primary flex items-center gap-1"><ChatCircleText size={13} /> {t("sharePage.reply")}</p>
      <Textarea
        data-testid={`share-reply-textarea-${capture.id}`}
        value={reply}
        onChange={(e) => {
          setReply(e.target.value);
          setSaved(false);
        }}
        placeholder={t("sharePage.replyPlaceholder")}
        className="rounded-none text-sm min-h-[64px]"
      />
      <Button
        data-testid={`share-reply-submit-${capture.id}`}
        size="sm"
        disabled={saving || !reply.trim() || saved}
        onClick={handleSubmit}
        className="rounded-none gap-2"
      >
        <PaperPlaneTilt size={14} /> {saved ? t("sharePage.replySubmitted") : t("sharePage.replySubmit")}
      </Button>
    </div>
  );
}

export default function SharePage() {
  const { token } = useParams();
  const { t } = useTranslation();
  const [captures, setCaptures] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    api
      .get(`/shares/${token}`)
      .then(({ data }) => setCaptures(data))
      .catch((e) => {
        if (e.response?.status === 410) {
          setErrorMessage(t("sharePage.expiredError"));
        } else {
          setErrorMessage(t("sharePage.notFoundError"));
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (errorMessage) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" data-testid="share-page-error">
        <p className="text-muted-foreground">{errorMessage}</p>
      </div>
    );
  }

  if (!captures) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" data-testid="share-page-loading">
        <p className="font-mono-tag text-sm text-muted-foreground animate-pulse">{t("sharePage.loading")}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background grain-overlay px-6 py-10 md:px-16" data-testid="share-page">
      <p className="font-mono-tag text-xs text-primary mb-2">{t("sharePage.tagline")}</p>
      <h1 className="font-display text-3xl font-black tracking-tight mb-8 flex items-center gap-3">
        <ImageSquare size={28} /> {t("sharePage.itemCount", { count: captures.length })}
      </h1>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl">
        {captures.map((c) => (
          <div key={c.id} className="border border-border bg-card flex flex-col" data-testid={`share-capture-${c.id}`}>
            {c.type === "text" ? (
              <div className="p-5 bg-secondary border-b border-border" data-testid={`share-text-content-${c.id}`}>
                <Quotes size={20} className="text-primary mb-2" weight="fill" />
                {c.text_content_html ? (
                  <div className="rich-text-content text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: c.text_content_html }} />
                ) : (
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">{c.text_content}</p>
                )}
              </div>
            ) : c.type === "pdf" ? (
              <div className="border-b border-border p-3 space-y-2" data-testid={`share-pdf-content-${c.id}`}>
                <PdfPreview pdfDataUrl={c.pdf_data} captureId={c.id} />
                <a
                  href={c.pdf_data}
                  download={`${c.title || "document"}.pdf`}
                  data-testid={`share-pdf-download-${c.id}`}
                  className="w-full inline-flex items-center justify-center gap-2 border border-border py-2 text-sm font-semibold hover:bg-secondary transition-colors"
                >
                  <DownloadSimple size={14} /> {t("dashboard.downloadPdf")}
                </a>
              </div>
            ) : (
              <img src={c.image_data} alt={c.title} className="w-full border-b border-border" />
            )}
            <div className="p-4 space-y-3 flex-1 flex flex-col">
              <div>
                <p className="font-semibold text-sm">{c.title}</p>
                {c.comment && <p className="text-xs text-muted-foreground whitespace-pre-wrap mt-1">{c.comment}</p>}
              </div>
              <div className="mt-auto">
                <ReplyBox token={token} capture={c} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
